import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Pencil, Plus } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useHabitContext } from '../../context/HabitContext';
import { useSelectedDate } from '../../context/SelectedDateContext';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { AddHabitModal, EditHabitPopover } from '../common/HabitModals';
import HabitChips from './HabitChips';
import TodoList from './TodoList';
import DailyCalendar from './DailyTimeline';
import SectionHeader from '../common/SectionHeader';
import NotesModal from './NotesModal';
import MonthlyFocusCard from './MonthlyFocusCard';
import Spinner from '../common/Spinner';
import { useToast } from '../common/Toast';
import type { DailySectionKey } from '../../types/onboarding';
import { DEFAULT_DAILY_SECTIONS, DEFAULT_HIDDEN_SECTIONS } from '../../types/onboarding';
import { isBlankNoteHtml, sanitizeHtml, htmlMapHasContent } from '../../utils/sanitizeHtml';
import { toLocalDateStr } from '../../utils/dateUtils';
import './DailyTracker.scss';

const DailyTracker: React.FC = () => {
  const { formatMessage: t } = useIntl();
  const { showToast } = useToast();
  const { selectedDate } = useSelectedDate();
  const {
    categories,
    isLoading: habitsLoading,
    getHabitEntriesFor,
    toggleHabitOn,
    setRatingOn,
    addHabit,
  } = useHabitContext();
  const { settings, updateSettings } = useUserPreferences();

  const sectionOrder = settings.dailyLayout?.sections ?? DEFAULT_DAILY_SECTIONS;
  const hiddenSections = settings.dailyLayout?.hiddenSections ?? DEFAULT_HIDDEN_SECTIONS;
  const hiddenSet = useMemo(() => new Set(hiddenSections), [hiddenSections]);
  const calendarHidden = hiddenSet.has('calendar');
  const focusHidden = hiddenSet.has('focus');

  const [showAddHabit, setShowAddHabit] = useState(false);
  const [showEditHabit, setShowEditHabit] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [scheduleTaskTime, setScheduleTaskTime] = useState<string | null>(null);

  const dateStr = toLocalDateStr(selectedDate);

  const typeOrder: Record<string, number> = { checkbox: 0, rating: 1 };
  const allHabits = categories
    .flatMap((c) => c.habits)
    .sort((a, b) => (typeOrder[a.type] ?? 0) - (typeOrder[b.type] ?? 0));

  const isHabitActiveOnDate = (habit: (typeof allHabits)[0], date: Date = selectedDate) => {
    if (!habit.createdAt) return true;
    const created = new Date(habit.createdAt);
    created.setHours(0, 0, 0, 0);
    const sel = new Date(date);
    sel.setHours(0, 0, 0, 0);
    return created <= sel;
  };

  const trackerHabits = allHabits.filter((h) => isHabitActiveOnDate(h));
  const hasTrackerHabits = trackerHabits.length > 0;
  const month = selectedDate.getMonth();
  const year = selectedDate.getFullYear();
  const dayNum = selectedDate.getDate();

  const entriesCache = Object.fromEntries(
    allHabits.map((h) => [h.id, getHabitEntriesFor(h.id, month, year)]),
  );

  const completedHabits = trackerHabits.filter((habit) => {
    const entry = entriesCache[habit.id]?.find((e) => e.day === dayNum);
    if (habit.type === 'checkbox') return entry?.completed === true;
    if (habit.type === 'rating') return (entry?.rating ?? 0) > 0;
    return false;
  }).length;
  const allHabitsDone = hasTrackerHabits && completedHabits === trackerHabits.length;
  const [habitsCollapsed, setHabitsCollapsed] = useState(allHabitsDone);

  useEffect(() => {
    if (allHabitsDone) setHabitsCollapsed(true);
  }, [allHabitsDone]);

  const noteKey = dateStr;
  const notesHtml = settings.dailyNotes?.[noteKey] ?? '';
  const notesFirstTime = !htmlMapHasContent(settings.dailyNotes);
  const habitsFirstTime = allHabits.length === 0;

  const saveDailyNote = async (html: string) => {
    await updateSettings({
      dailyNotes: {
        ...(settings.dailyNotes ?? {}),
        [noteKey]: html,
      },
    });
  };

  const sectionRenderers: Record<DailySectionKey, () => React.ReactNode> = {
    habits: () => (
      <div
        className={`daily-card daily-card--habits${habitsCollapsed && allHabitsDone ? ' daily-card--complete' : ''}`}
        key="habits"
      >
        {habitsCollapsed && allHabitsDone ? (
          <button
            type="button"
            className="daily-complete-banner"
            onClick={() => setHabitsCollapsed(false)}
            aria-expanded={false}
          >
            <Check className="daily-complete-banner__icon" size={16} strokeWidth={2.5} />
            <span className="daily-complete-banner__label">{t({ id: 'daily.habits.allDone' })}</span>
            <ChevronDown className="daily-complete-banner__chevron" size={16} />
          </button>
        ) : (
          <>
            <SectionHeader
              title={t({ id: 'daily.habits.title' })}
              count={
                hasTrackerHabits
                  ? t({ id: 'daily.habits.count' }, { completed: completedHabits, total: trackerHabits.length })
                  : undefined
              }
              collapsed={habitsCollapsed}
              onToggle={hasTrackerHabits ? () => setHabitsCollapsed((c) => !c) : undefined}
              toggleLabel={
                habitsCollapsed ? t({ id: 'daily.habits.expand' }) : t({ id: 'daily.habits.collapse' })
              }
              actions={
                habitsCollapsed ? undefined : (
                  <>
                    <button
                      className="daily-header__icon-btn"
                      onClick={() => {
                        if (hasTrackerHabits) setShowEditHabit(true);
                      }}
                      aria-label={t({ id: 'daily.habit.edit' })}
                      title={t({ id: 'daily.habit.edit' })}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      className="daily-header__icon-btn"
                      onClick={() => setShowAddHabit(true)}
                      aria-label={t({ id: 'daily.habit.add' })}
                      title={t({ id: 'daily.habit.add' })}
                    >
                      <Plus size={15} />
                    </button>
                  </>
                )
              }
            />
            {habitsCollapsed ? null : habitsLoading ? (
              <Spinner block />
            ) : hasTrackerHabits ? (
              <HabitChips
                habits={trackerHabits}
                day={selectedDate.getDate()}
                getEntry={(habitId, day) => entriesCache[habitId]?.find((e) => e.day === day)}
                onToggle={(habitId, day) => toggleHabitOn(habitId, day, month, year)}
                onRate={(habitId, day, rating) => setRatingOn(habitId, day, rating, month, year)}
              />
            ) : (
              <p className="daily-habits-empty empty-copy">
                <span className="empty-copy__body">
                  {t({ id: habitsFirstTime ? 'daily.habits.empty' : 'daily.habits.emptyShort' })}
                </span>
                {habitsFirstTime ? (
                  <span className="empty-copy__example">{t({ id: 'daily.habits.emptyExample' })}</span>
                ) : null}
              </p>
            )}
          </>
        )}
      </div>
    ),
    notes: () => (
      <div className="daily-card daily-card--notes" key="notes">
        <SectionHeader title={t({ id: 'daily.notes.dailyTitle' })} />
        {habitsLoading ? (
          <Spinner block />
        ) : isBlankNoteHtml(notesHtml) ? (
          <button
            type="button"
            className="daily-notes-preview"
            onClick={() => setNotesOpen(true)}
          >
            <span className="empty-copy__body">
              {t({ id: notesFirstTime ? 'daily.notes.placeholder' : 'daily.notes.placeholderShort' })}
            </span>
            {notesFirstTime ? (
              <span className="empty-copy__example">{t({ id: 'daily.notes.placeholderExample' })}</span>
            ) : null}
          </button>
        ) : (
          <button
            type="button"
            className="daily-notes-preview daily-notes-preview--lined note-html"
            onClick={() => setNotesOpen(true)}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(notesHtml) }}
          />
        )}
      </div>
    ),
    tasks: () => (
      <div className="daily-card daily-card--tasks" key="tasks">
        <TodoList
          selectedDate={selectedDate}
          createWithTime={scheduleTaskTime}
          onCreateWithTimeHandled={() => setScheduleTaskTime(null)}
        />
      </div>
    ),
  };

  const visibleSections = sectionOrder.filter((key) => !hiddenSet.has(key));
  const showRail = !focusHidden || !calendarHidden;
  const layoutCls = [
    'daily-tracker__layout',
    !showRail ? 'daily-tracker__layout--no-rail' : '',
    visibleSections.length === 0 ? 'daily-tracker__layout--rail-only' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="daily-tracker">
      {(visibleSections.length > 0 || showRail) && (
        <div className={layoutCls}>
          <div className="daily-tracker__main">
            {visibleSections.length > 0 && (
              <div className="daily-tracker__stack">
                {visibleSections.map((key) => sectionRenderers[key]())}
              </div>
            )}
            {showRail && (
              <div className="daily-tracker__rail" data-testid="daily-tracker-rail">
                {!focusHidden && <MonthlyFocusCard date={selectedDate} />}
                {!calendarHidden && (
                  <div className="daily-tracker__calendar">
                    <DailyCalendar date={dateStr} onEmptySlotClick={setScheduleTaskTime} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showAddHabit && (
        <AddHabitModal
          onAdd={(name, type, color, temporary, scheduledTime) =>
            addHabit(name, type, color, temporary, scheduledTime)
          }
          onClose={() => setShowAddHabit(false)}
        />
      )}

      {showEditHabit && (
        <EditHabitPopover
          habit={trackerHabits[0]}
          habits={trackerHabits}
          onClose={() => setShowEditHabit(false)}
        />
      )}

      <NotesModal
        isOpen={notesOpen}
        onClose={() => setNotesOpen(false)}
        notes={notesHtml}
        onSave={async (html) => {
          try {
            await saveDailyNote(html);
            showToast(t({ id: 'toast.note.saved' }));
          } catch {
            showToast(t({ id: 'toast.note.saveFailed' }), 'error');
            throw new Error('save failed');
          }
        }}
        title={t({ id: 'daily.notes.dailyTitle' })}
        placeholder={t({ id: notesFirstTime ? 'daily.notes.placeholder' : 'daily.notes.placeholderShort' })}
        maxLength={150}
      />
    </div>
  );
};

export default DailyTracker;
